/**
 * GitBookWindowManager
 *
 * Manages a single GitBook browser tab/window.
 * - Reuses the same tab when the user clicks a help link multiple times
 * - Provides a method to navigate the GitBook tab to its logout URL
 *   (used on app logout, session timeout, or tab close)
 */

import { useCallback } from "react";
import { useRef } from "react";

const GITBOOK_WINDOW_NAME = "ifx_gitbook_help";

// The GitBook logout path — adjust the base URL if your GitBook site uses a custom domain
// This will be appended to the GitBook site origin derived from the last opened URL.
const GITBOOK_LOGOUT_PATH = "/~gitbook/auth/logout";

export const useGitBookManager = () => {
  const gitBookRef = useRef({ gitBookWindow: null, lastGitBookOrigin: null });

  const setGitBookWindow = useCallback(gitBookWindow => {
    gitBookRef.current.gitBookWindow = gitBookWindow;
  }, []);

  const clearGitBookWindow = useCallback(() => {
    gitBookRef.current.gitBookWindow = null;
  }, []);

  const setLastGitBookOrigin = useCallback(lastGitBookOrigin => {
    gitBookRef.current.lastGitBookOrigin = lastGitBookOrigin;
  }, []);

  const clearLastGitBookOrigin = useCallback(() => {
    gitBookRef.current.lastGitBookOrigin = null;
  }, []);

  /**
   * Opens a help link URL in a single reusable GitBook tab.
   * If the tab is already open, navigates it to the new URL and focuses it.
   *
   * @param {string} url - The fully qualified help access URL (with signed JWT redirect)
   */
  const openGitBookHelp = useCallback(url => {
    const helpWindow = gitBookRef.current.gitBookWindow;

    if (helpWindow && !helpWindow.closed) {
      helpWindow.location.href = url;
      helpWindow.focus();
    } else {
      setGitBookWindow(window.open(url, GITBOOK_WINDOW_NAME));
    }

    // Try to extract the GitBook origin from the target URL for logout purposes
    try {
      const targetUrlParam = new URL(url, window.location.origin);
      const targetUrl = targetUrlParam.searchParams?.get("targetUrl");
      if (targetUrl) {
        const gitbookUrl = new URL(targetUrl);
        setLastGitBookOrigin(gitbookUrl.origin);
      }
    } catch (e) {
      // URL parsing failed — not critical, logout will still attempt with stored origin
    }
  }, []);

  /**
   * Navigates the GitBook tab to its logout URL (if open), then optionally closes it.
   *
   * @param {Object} options
   * @param {boolean} [options.closeAfterLogout=true] - Whether to close the tab after logout
   * @param {number} [options.closeDelay=1500] - Delay in ms before closing (to allow logout redirect to fire)
   */
  const logoutGitBook = useCallback(
    ({ closeAfterLogout = true, closeDelay = 1500 } = {}) => {
      const helpWindow = gitBookRef.current.gitBookWindow;
      const lastGitBookOrigin = gitBookRef.current.lastGitBookOrigin;

      if (helpWindow && !helpWindow.closed) {
        if (lastGitBookOrigin) {
          helpWindow.location.href = lastGitBookOrigin + GITBOOK_LOGOUT_PATH;
        }

        if (closeAfterLogout) {
          setTimeout(() => {
            if (helpWindow && !helpWindow.closed) {
              helpWindow.close();
            }
            clearGitBookWindow();
          }, closeDelay);
        }
      } else {
        clearGitBookWindow();
      }
    },
    []
  );

  /**
   * Returns whether the GitBook help window is currently open.
   */
  const isGitBookWindowOpen = useCallback(() => {
    const helpWindow = gitBookRef.current.gitBookWindow;
    return helpWindow && !helpWindow.closed;
  }, []);

  /**
   * Cleans up the window reference (e.g., if the user manually closed it).
   */
  const resetGitBookWindow = useCallback(() => {
    clearGitBookWindow();
    clearLastGitBookOrigin();
  }, []);

  return {
    openGitBookHelp,
    logoutGitBook,
    isGitBookWindowOpen,
    resetGitBookWindow,
  };
};
